import { TemplateRef } from '@angular/core';

import { isNullable } from '@qtek/shared/utils';
import { FilterBase, FilterChangeBase } from './base';

interface SelectOption {
  /** Translation key for that option. */
  name: string;

  /** Value of select option. */
  value: any;
}

export interface SelectValues {
  options?: SelectOption[];
  selectedValue?: any | any[] | null;
}

export interface SelectFilter extends FilterBase<SelectValues> {
  type: string;

  operation?: string;

  /** Template for select trigger. */
  triggerTemplate?: TemplateRef<any>;

  /** Template for option name. */
  nameTemplate?: TemplateRef<any>;

  /** Whether select should accept mutiple values. */
  multiple?: boolean;

  /** Determines how to compare values selected by valueAccessor and filter output. */
  compare?: (value: any, filterValue: any) => boolean;
}

export type SelectFilterChange = FilterChangeBase<SelectValues, SelectFilter>;

function basicCompare(value: any, filterValue: any) {
  return value === filterValue;
}

/**
 * Predicate function for select filter.
 *
 * @param filters List of filter configs.
 * @param row Row of data to filter.
 * @param valueAccessor
 */
export function selectFilter<T>(
  filters: SelectFilterChange[],
  row: T,
  valueAccessor: (row: T, id: string) => any
): boolean {
  return filters.every(({ values, config }) => {
    const isMultiple = Array.isArray(values.selectedValue);

    /** Filter is empty. */
    if (
      isNullable(values.selectedValue) ||
      (isMultiple && !values.selectedValue.length)
    ) {
      return true;
    }

    const value = valueAccessor(row, config.id);

    if (isNullable(value)) {
      return false;
    }

    const compare = config.compare || basicCompare;

    if (isMultiple) {
      return values.selectedValue.some((v: any) => compare(value, v));
    }

    return compare(value, values.selectedValue);
  });
}
